"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEnvironmentKeyTemplate = getEnvironmentKeyTemplate;
exports.getRC4DecryptTemplate = getRC4DecryptTemplate;
exports.getLazyDecryptionTemplate = getLazyDecryptionTemplate;
exports.getBytecodeArrayEncodingTemplate = getBytecodeArrayEncodingTemplate;
exports.getDirectBytecodeAccessTemplate = getDirectBytecodeAccessTemplate;
exports.getBigIntPreprocessingTemplate = getBigIntPreprocessingTemplate;
exports.rc4Encrypt = rc4Encrypt;
exports.computeEnvironmentKey = computeEnvironmentKey;
function getEnvironmentKeyTemplate() {
    return `
            // Derive encryption key from runtime environment
            // These values are hard to predict statically
            function _deriveEnvKey() {
                let key = 0;
                // Use Function.prototype.toString characteristics
                try {
                    key ^= (Function.prototype.toString.call(Array.prototype.push).length << 16);
                    key ^= (Function.prototype.toString.call(Object.keys).length << 8);
                    key ^= Function.prototype.toString.call(Math.abs).length;
                } catch (e) {
                    key ^= 0x12_34_56_78;
                }
                // Use built-in function arity
                key ^= (Array.prototype.slice.length << 24);
                key ^= (String.prototype.charAt.length << 20);
                // Mix bits
                key ^= (key >>> 16);
                key = Math.imul(key, 0x85_EB_CA_6B) >>> 0;
                key ^= (key >>> 13);
                key = Math.imul(key, 0xC2_B2_AE_35) >>> 0;
                key ^= (key >>> 16);
                return key >>> 0;
            }`;
}
function getRC4DecryptTemplate() {
    return `
            // RC4 decryption
            function _rc4Decrypt(data, key) {
                let s = [];
                let j = 0;
                let result = '';
                // Key scheduling
                for (let i = 0; i < 256; i++) {
                    s[i] = i;
                }
                for (let i = 0; i < 256; i++) {
                    j = (j + s[i] + key.charCodeAt(i % key.length)) & 255;
                    let tmp = s[i];
                    s[i] = s[j];
                    s[j] = tmp;
                }
                // Decryption
                let i = 0;
                j = 0;
                for (let k = 0; k < data.length; k++) {
                    i = (i + 1) & 255;
                    j = (j + s[i]) & 255;
                    let tmp = s[i];
                    s[i] = s[j];
                    s[j] = tmp;
                    result += String.fromCharCode(data.charCodeAt(k) ^ s[(s[i] + s[j]) & 255]);
                }
                return result;
            }`;
}
function getLazyDecryptionTemplate() {
    return `
            // Lazy decryption - decrypt each bytecode only when needed
            let _envKey = _deriveEnvKey();
            let _keyStr = _envKey.toString(16);

            function _getBytecode(index) {
                let entry = {vmBytecodeArrayName}[index];
                if (typeof entry === 'string') {
                    // Decode base64 and decrypt
                    let decoded = atob(entry);
                    let decrypted = _rc4Decrypt(decoded, _keyStr);
                    // Decode UTF-8 bytes to string (decrypted contains UTF-8 bytes as char codes)
                    let jsonStr = decodeURIComponent(escape(decrypted));
                    // Parse JSON and cache
                    let bc = JSON.parse(jsonStr);
                    // BigInt preprocessing for constants
                    if (bc[{keyC}]) {
                        for (let cIdx = 0; cIdx < bc[{keyC}].length; cIdx++) {
                            let constVal = bc[{keyC}][cIdx];
                            if (typeof constVal === 'string' && constVal.length > 1 && constVal[constVal.length - 1] === 'n') {
                                try {
                                    bc[{keyC}][cIdx] = BigInt(constVal.slice(0, -1));
                                } catch (e) {}
                            }
                        }
                    }
                    {vmBytecodeArrayName}[index] = bc;
                }
                return {vmBytecodeArrayName}[index];
            }`;
}
function getBytecodeArrayEncodingTemplate() {
    return getEnvironmentKeyTemplate() + getRC4DecryptTemplate() + getLazyDecryptionTemplate();
}
function getDirectBytecodeAccessTemplate() {
    return `
            function _getBytecode(index) {
                return {vmBytecodeArrayName}[index];
            }`;
}
function getBigIntPreprocessingTemplate() {
    return `
            // Pre-process constants: Convert BigInt strings (ending with 'n') to BigInt objects
            // This is done once at initialization instead of on every PUSH_CONST for better performance
            for (let bcIdx = 0; bcIdx < {vmBytecodeArrayName}.length; bcIdx++) {
                let bc = {vmBytecodeArrayName}[bcIdx];
                if (bc[{keyC}]) {
                    for (let cIdx = 0; cIdx < bc[{keyC}].length; cIdx++) {
                        let constVal = bc[{keyC}][cIdx];
                        if (typeof constVal === 'string' && constVal.length > 1 && constVal[constVal.length - 1] === 'n') {
                            try {
                                bc[{keyC}][cIdx] = BigInt(constVal.slice(0, -1));
                            } catch (e) {
                                // Not a valid BigInt, leave as string
                            }
                        }
                    }
                }
            }`;
}
function rc4Encrypt(data, key) {
    const dataBytes = Buffer.from(data, 'utf8');
    const s = [];
    let j = 0;
    for (let i = 0; i < 256; i++) {
        s[i] = i;
    }
    for (let i = 0; i < 256; i++) {
        j = (j + s[i] + key.charCodeAt(i % key.length)) & 255;
        const tmp = s[i];
        s[i] = s[j];
        s[j] = tmp;
    }
    let i = 0;
    j = 0;
    const result = Buffer.alloc(dataBytes.length);
    for (let k = 0; k < dataBytes.length; k++) {
        i = (i + 1) & 255;
        j = (j + s[i]) & 255;
        const tmp = s[i];
        s[i] = s[j];
        s[j] = tmp;
        result[k] = dataBytes[k] ^ s[(s[i] + s[j]) & 255];
    }
    return result;
}
function computeEnvironmentKey() {
    let key = 0;
    try {
        key ^= (Function.prototype.toString.call(Array.prototype.push).length << 16);
        key ^= (Function.prototype.toString.call(Object.keys).length << 8);
        key ^= Function.prototype.toString.call(Math.abs).length;
    }
    catch (_a) {
        key ^= 305419896;
    }
    key ^= (Array.prototype.slice.length << 24);
    key ^= (String.prototype.charAt.length << 20);
    key ^= (key >>> 16);
    key = Math.imul(key, 2246822507) >>> 0;
    key ^= (key >>> 13);
    key = Math.imul(key, 3266489909) >>> 0;
    key ^= (key >>> 16);
    return key >>> 0;
}
